import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { FormSubmitService } from './form-submit.service';
import { FilingFormState, FilingFormStateService } from './filing-form-state.service';
import { FilingStore } from './filling-store.service';
import { CurrentSheetCacheService } from './current-sheet-cache.service';
import { buildSheetToDtoMap } from '../../models/sheet-descriptors';
import { normalizeApiErrors } from '../../utils/api-errors.util';

/**
 * Discriminated result of a `submit-for-validation` call.
 *
 * Three distinct outcomes the caller must branch on (per backend contract
 * documented in role3-date-field-repopulation-frontend-fix-20260424.md):
 *
 *  - `success`          : HTTP 200, body `status: true`, body `statusCode: 200`.
 *                         Payload persisted, validation passed.
 *  - `validation-failed`: HTTP 200, body `status: false`, body `statusCode` 4xx.
 *                         Payload STILL persisted — validation errors are
 *                         informational. Caller should commit local state and
 *                         redirect as if success, but surface a warning toast.
 *  - `error`            : Transport/infrastructure fault (HTTP 4xx/5xx thrown
 *                         by HttpClient, timeout, network error, or empty
 *                         payload). Payload NOT persisted — caller must keep
 *                         the user on the form and let them retry.
 */
export type SubmitForValidationResult =
  | { tag: 'success'; message?: string }
  | { tag: 'validation-failed'; errors: string[]; statusCode?: number }
  | {
      tag: 'error';
      cause: 'http' | 'empty-payload';
      httpStatus?: number;
      /** Backend `errorCode` from the refused request body, when it sent one. */
      errorCode?: string;
    };

/**
 * Reusable service for handling form validation submissions across all sheets
 * This service provides a standardized way to submit form data to the backend
 * and handle responses consistently.
 */
@Injectable({
  providedIn: 'root',
})
export class FormValidationSubmitService {
  private formSubmitService = inject(FormSubmitService);
  private stateService = inject(FilingFormStateService);
  private filingStore = inject(FilingStore);
  private currentSheetCache = inject(CurrentSheetCacheService);

  /**
   * Submit form data for validation to BACKEND with custom data (e.g., only visible fields)
   *
   * Returns a discriminated result so the caller can distinguish validation
   * failures (payload persisted, user may proceed) from transport faults
   * (payload NOT persisted, user must retry). Toast decisions live on the
   * caller because the three branches need different messaging.
   *
   * @param form - The FormGroup (used for reference, not validation)
   * @param formData - The data to submit (can be filtered/custom data)
   * @param sheetKey - The sheet identifier
   */
  async submitForValidationWithData(
    form: FormGroup,
    formData: any,
    sheetKey: keyof FilingFormState,
  ): Promise<SubmitForValidationResult> {
    if (!formData || Object.keys(formData).length === 0) {
      return { tag: 'error', cause: 'empty-payload' };
    }

    try {
      const payload = this.prepareSubmissionPayload(formData, sheetKey);

      // An unmapped sheetKey yields no payload (setDtoProperty warns and
      // returns undefined). Never POST an empty body: report it like any
      // other empty payload so the caller keeps the user on the form.
      if (!payload) {
        return { tag: 'error', cause: 'empty-payload' };
      }

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log("[On Form Validation Submission] Payload To Submit to the BE API Endpoint: ", payload);

      const response = await firstValueFrom(
        this.formSubmitService.submitForValidation(payload)
      );

      // Either persisted branch (success or validation-failed) ends the
      // user's editing session for this conversion; drop the per-sheet
      // pointer so the next visit starts fresh.
      const conversionId = this.stateService.getConversionId();

      // HTTP 200 + body.status === true → validation passed, payload persisted.
      if (response.status) {
        this.currentSheetCache.clear(conversionId);
        return { tag: 'success', message: response.data?.message };
      }

      // HTTP 200 + body.status === false → validation failed, payload STILL
      // persisted per the backend contract. Caller should proceed (commit
      // local state, redirect) but surface a warning with the error list.
      this.currentSheetCache.clear(conversionId);
      return {
        tag: 'validation-failed',
        // `errors` is typed string[] but can arrive as a bare string.
        errors: normalizeApiErrors(response.errors),
        statusCode: response.statusCode,
      };
    } catch (error: any) {
      // Transport/infrastructure fault — HttpClient threw. Payload was not
      // persisted; caller must keep the user on the form.
      console.error(`Error submitting ${sheetKey} for validation:`, error);
      return {
        tag: 'error',
        cause: 'http',
        httpStatus: error?.status,
        ...(typeof error?.error?.errorCode === 'string' && error.error.errorCode
          ? { errorCode: error.error.errorCode as string }
          : {}),
      };
    }
  }

  /**
   * Prepare form data payload for backend submission
   * Transforms to backend XbrlDataDto pattern with MFRS/MPERS specific DTOs
   *
   * Backend expects: { ConversionId: "...", FilingInfoDto: {...}, MfrsDirectorsRepDto: {...}, ... }
   *
   * @param formData - Filtered form data (e.g., from getVisibleFormData())
   * @param sheetKey - The sheet identifier
   * @returns Formatted payload for backend XbrlDataDto pattern
   */
  private prepareSubmissionPayload(
    formData: any,
    sheetKey: string,
  ): any {
    // Get current conversionId from state service
    const conversionId = this.stateService.getConversionId();

    // Determine if MFRS or MPERS from FilingInfoDto in formData
    const taxonomyType = this.getTaxonomyType(formData);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📦 Preparing payload for ${sheetKey} (${taxonomyType}) with ${Object.keys(formData).length} fields`);
    // console.log('📦 Payload data:', formData);

    // Format all sepCur, sepPrev, conCur, conPrev values to strings
    this.formatDimensionalValuesToString(formData);

    // Set the appropriate DTO property based on sheetKey and taxonomyType
    // This modifies formData in place and returns it
    return this.setDtoProperty(sheetKey, taxonomyType, formData, conversionId);
  }

  /**
   * Format all sepCur, sepPrev, conCur, conPrev values to strings
   * Recursively processes all nested objects and converts these specific keys to strings
   *
   * @param data - The data object to process
   */
  private formatDimensionalValuesToString(data: any): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    // Process each key in the object
    for (const key in data) {
      if (!data.hasOwnProperty(key)) {
        continue;
      }

      const value = data[key];

      // If it's an object with sepCur, sepPrev, conCur, conPrev properties
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Check if this object has dimensional properties
        const hasDimensionalProps =
          value.hasOwnProperty('sepCur') ||
          value.hasOwnProperty('sepPrev') ||
          value.hasOwnProperty('conCur') ||
          value.hasOwnProperty('conPrev');

        if (hasDimensionalProps) {
          // Convert all dimensional values to strings
          if (value.sepCur !== undefined && value.sepCur !== null) {
            value.sepCur = String(value.sepCur);
          }
          if (value.sepPrev !== undefined && value.sepPrev !== null) {
            value.sepPrev = String(value.sepPrev);
          }
          if (value.conCur !== undefined && value.conCur !== null) {
            value.conCur = String(value.conCur);
          }
          if (value.conPrev !== undefined && value.conPrev !== null) {
            value.conPrev = String(value.conPrev);
          }
        } else {
          // Recursively process nested objects
          this.formatDimensionalValuesToString(value);
        }
      }
    }
  }

  /**
   * Transform formData to backend DTO format
   * Handles both taxonomy-agnostic DTOs (FilingInfo) and taxonomy-specific DTOs (MFRS/MPERS)
   *
   * @param sheetKey - The sheet identifier
   * @param taxonomyType - 'MFRS' or 'MPERS'
   * @param formData - The form data to transform
   * @param conversionId - The conversion ID to include in the payload
   * @returns Transformed formData with DTO property names
   */
  private setDtoProperty(
    sheetKey: string,
    taxonomyType: 'MFRS' | 'MPERS',
    formData: any,
    conversionId: string
  ): any {
    // Mapping of sheetKey to DTO property names (matching backend C# class properties)
    const sheetToDtoMap: Record<string, { mfrsDto?: string; mpersDto?: string; commonDto?: string }> = buildSheetToDtoMap();

    const mapping = sheetToDtoMap[sheetKey];

    if (!mapping) {
      console.warn(`⚠️ No DTO mapping found for sheetKey: ${sheetKey}`);
      return;
    }

    for (const [sheetName, dtoMap] of Object.entries(sheetToDtoMap)) {
      // Skip if the sheet doesn't exist in formData
      if (!formData[sheetName]) {
        continue;
      }

      // Check if the sheet data is an empty object and skip it (don't map it)
      if (typeof formData[sheetName] === 'object' &&
          formData[sheetName] !== null &&
          Object.keys(formData[sheetName]).length === 0) {
        delete formData[sheetName];
        continue;
      }

      // Map to DTO property name only if data exists and is not empty
      if (dtoMap.commonDto) {
        formData[dtoMap.commonDto!] = formData[sheetName];
        delete formData[sheetName];
        continue;
      }

      if (taxonomyType == "MFRS" && dtoMap.mfrsDto) {
        formData[dtoMap.mfrsDto!] = formData[sheetName];
      }

      if (taxonomyType == "MPERS" && dtoMap.mpersDto) {
        formData[dtoMap.mpersDto!] = formData[sheetName];
      }

      delete formData[sheetName];
    }

    // Remove any remaining empty objects from the final payload
    for (const key of Object.keys(formData)) {
      if (typeof formData[key] === 'object' &&
          formData[key] !== null &&
          Object.keys(formData[key]).length === 0) {
        delete formData[key];
      }
    }

    // Add ConversionId to the formData
    formData.ConversionId = conversionId;

    // Return formData directly (no wrapper)
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('📦 Final transformed payload:', formData);
    
    return formData;
  }

  /**
   * Determine if the current filing is MFRS or MPERS
   * Checks the FilingInfoDto or filingPrerequisites data in formData for accounting standards field
   * @param formData - The form data containing FilingInfoDto or filingPrerequisites
   * @returns 'MFRS' or 'MPERS'
   */
  private getTaxonomyType(formData: any): 'MFRS' | 'MPERS' {
    // Try to get accounting standards from multiple possible sources in formData
    let accountingStandard = '';

    // Check filingInfoDto (if present in formData)
    if (formData.filingInfoDto) {
      accountingStandard = formData.filingInfoDto['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';
    }

    // Check filingInfo (alternative key name)
    if (!accountingStandard && formData.filingInfo) {
      accountingStandard = formData.filingInfo['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';
    }

    // Check filingPrerequisites (alternative key name)
    if (!accountingStandard && formData.filingPrerequisites) {
      accountingStandard = formData.filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';
    }

    // If still not found, try to get from state service as fallback
    if (!accountingStandard) {
      const filingData = this.stateService.getActiveFilingInfoData();
      accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('📋 Accounting Standard detected:', accountingStandard);

    // Check if it's MPERS
    if (accountingStandard === 'Malaysian Private Entities Reporting Standards') {
      return 'MPERS';
    }

    // Check if it's MFRS (explicit check)
    if (accountingStandard === 'Malaysian Financial Reporting Standards') {
      return 'MFRS';
    }

    // Fallback: Check FilingStore as last resort
    const filingState = this.filingStore.snapshot;
    if (filingState.taxonomyPath.toLowerCase().includes('mpers') ||
        filingState.entrypointXsd.toLowerCase().includes('mpers')) {
      return 'MPERS';
    }

    // Default to MFRS if no clear indication
    console.warn('⚠️ Could not determine accounting standard, defaulting to MFRS');
    return 'MFRS';
  }
}
